// src/utils/vendorDirectory.js
// Shared, team-wide vendor directory (title, inspector, plumber, electrician,
// etc.). Any agent can add; everyone sees the same live list. Stored as a
// Firestore collection so concurrent adds don't clobber each other.
import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDoc,
  onSnapshot,
  serverTimestamp,
  arrayUnion,
} from "firebase/firestore";
import { firestored } from "../firebase/firebaseConfig";
import { replaceNoteByRef, removeNoteByRef } from "./entryNotes";

const COL = "vendorDirectory";

// Live subscription. Returns the unsubscribe fn.
export function listenVendors(cb) {
  return onSnapshot(
    collection(firestored, COL),
    (snap) => cb(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
    (err) => console.error("listenVendors failed:", err),
  );
}

export async function addVendor(vendor, userId) {
  return addDoc(collection(firestored, COL), {
    ...vendor,
    createdBy: userId || null,
    createdAt: serverTimestamp(),
  });
}

export async function updateVendor(id, vendor) {
  return updateDoc(doc(firestored, COL, id), vendor);
}

// Appends an attributed note. arrayUnion so two agents adding notes at the same
// time can't clobber each other. Build the note with buildNote() from
// utils/entryNotes so the author and timestamp are stamped in (serverTimestamp
// can't be used inside an array element).
export async function addVendorNote(id, note) {
  return updateDoc(doc(firestored, COL, id), {
    noteEntries: arrayUnion(note),
  });
}

// Edits and deletes re-read the vendor first and address the note by its
// identity (see utils/entryNotes), so a note someone else just added can't
// be clobbered by an old client-side copy. Both return the fresh array that
// was saved, or null when the note was already gone.
async function rewriteVendorNotes(id, apply) {
  const ref = doc(firestored, COL, id);
  const snap = await getDoc(ref);
  const current = snap.exists() ? snap.data().noteEntries || [] : [];
  const next = apply(current);
  if (next === null) return null;
  await updateDoc(ref, { noteEntries: next });
  return next;
}

export async function editVendorNote(id, noteRef, newText) {
  return rewriteVendorNotes(id, (cur) =>
    replaceNoteByRef(cur, noteRef, newText),
  );
}

export async function deleteVendorNote(id, noteRef) {
  return rewriteVendorNotes(id, (cur) => removeNoteByRef(cur, noteRef));
}

// Set (or change) one person's 1..5 star vote without touching anyone
// else's. Refuses to write without a real user id — a phantom "undefined"
// key would count in the team average forever.
export async function setVendorRating(id, userId, stars) {
  if (!userId) throw new Error("setVendorRating: no user id");
  return updateDoc(doc(firestored, COL, id), {
    [`ratings.${userId}`]: stars,
  });
}

export async function removeVendor(id) {
  return deleteDoc(doc(firestored, COL, id));
}
